import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Delete,
  Edit,
  PlayArrow,
  Repeat,
  Stop,
} from '@mui/icons-material';

export default function RecurringExpensesTab({
  recurringExpenses,
  categories,
  formatCurrency,
  onAdd,
  onEdit,
  onDelete,
  onToggle,
  isMobile,
}) {
  return (
    <>
      {/* Header banner */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2.5,
        p: 2, borderRadius: 3, flexWrap: 'wrap',
        background: 'linear-gradient(135deg, rgba(139,92,246,0.04) 0%, rgba(124,58,237,0.08) 100%)',
        border: '1px solid rgba(139,92,246,0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Repeat sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Επαναλαμβανόμενα Έξοδα
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Δημιουργούνται αυτόματα κάθε μήνα
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          onClick={onAdd}
        >
          Νέο Επαναλαμβανόμενο
        </Button>
      </Box>

      {/* Recurring cards layout */}
      <Stack spacing={1.5}>
        {recurringExpenses.map((recurring) => (
          <Card key={recurring.id} sx={{
            borderRadius: 3,
            borderLeft: `4px solid ${recurring.active ? '#8b5cf6' : '#94a3b8'}`,
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            opacity: recurring.active ? 1 : 0.6,
            transition: 'all 0.2s ease',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }
          }}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                {/* Left: Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 200 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {recurring.category_name || 'Χωρίς κατηγορία'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {recurring.description || 'Χωρίς περιγραφή'}
                      {recurring.last_generated_date && ` — Τελ.: ${recurring.last_generated_date}`}
                    </Typography>
                  </Box>
                </Box>

                {/* Center: Stats */}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(139,92,246,0.06)', borderRadius: 2, textAlign: 'center', minWidth: 80 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Ποσό</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {formatCurrency(recurring.total_amount || recurring.amount)}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(59,130,246,0.06)', borderRadius: 2, textAlign: 'center', minWidth: 80 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Ημέρα</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{recurring.day_of_month}η</Typography>
                  </Box>
                  <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(34,197,94,0.06)', borderRadius: 2, textAlign: 'center', minWidth: 80 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Πρόοδος</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {recurring.total_occurrences ? `${recurring.occurrences_count || 0}/${recurring.total_occurrences}` : '∞'}
                    </Typography>
                  </Box>
                  {recurring.active ? (
                    <Chip icon={<CheckCircle sx={{ fontSize: 16 }} />} label="Ενεργό" color="success" size="small" sx={{ fontWeight: 600, borderRadius: '8px', alignSelf: 'center' }} />
                  ) : (
                    <Chip icon={<Stop sx={{ fontSize: 16 }} />} label="Ανενεργό" color="default" size="small" sx={{ fontWeight: 600, borderRadius: '8px', alignSelf: 'center' }} />
                  )}
                </Box>

                {/* Right: Actions */}
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="small"
                    onClick={() => onToggle(recurring.id)}
                    title={recurring.active ? 'Απενεργοποίηση' : 'Ενεργοποίηση'}
                    sx={{ bgcolor: recurring.active ? 'rgba(234,179,8,0.08)' : 'rgba(34,197,94,0.08)', borderRadius: 2, '&:hover': { bgcolor: recurring.active ? 'rgba(234,179,8,0.15)' : 'rgba(34,197,94,0.15)' } }}
                  >
                    {recurring.active ? <Stop sx={{ fontSize: 20, color: '#ca8a04' }} /> : <PlayArrow sx={{ fontSize: 20, color: '#16a34a' }} />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onEdit(recurring)}
                    sx={{ bgcolor: 'rgba(59,130,246,0.08)', borderRadius: 2, '&:hover': { bgcolor: 'rgba(59,130,246,0.15)' } }}
                  >
                    <Edit sx={{ fontSize: 18, color: '#3b82f6' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onDelete(recurring.id)}
                    sx={{ bgcolor: 'rgba(239,68,68,0.08)', borderRadius: 2, '&:hover': { bgcolor: 'rgba(239,68,68,0.15)' } }}
                  >
                    <Delete sx={{ fontSize: 18, color: '#ef4444' }} />
                  </IconButton>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        ))}
        {recurringExpenses.length === 0 && (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Repeat sx={{ fontSize: 56, color: 'grey.300', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
              Δεν υπάρχουν επαναλαμβανόμενα έξοδα
            </Typography>
          </Box>
        )}
      </Stack>
    </>
  );
}
